import { DrillScoring } from '@core';
import { RunViewModel, SessionDrillViewModel } from '../../application/models';
import { SessionRunRow } from './session-rep-list.types';

/**
 * Buduje wiersze pojedynczych runów dla drilla w sesji.
 * Formatowanie jest wspólne dla ekranów aktywnej sesji (run-entry) oraz podglądu podsumowania.
 */
export const buildRunRows = (sd: SessionDrillViewModel, repsUnit: string): readonly SessionRunRow[] => {
  switch (sd.scoring) {
    case DrillScoring.TimePerRep:
      return sd.runs.map(
        (r: RunViewModel, i: number): SessionRunRow => ({
          id: r.id,
          index: i + 1,
          value: r.timeSeconds === null ? '-' : r.timeSeconds.toFixed(2),
          unit: 's',
          best: r.isBest
        })
      );
    case DrillScoring.HitFactor:
      return sd.runs.map((r: RunViewModel, i: number): SessionRunRow => {
        const hfLabel: string = r.hitFactor === null ? '-' : `HF ${r.hitFactor.toFixed(2)}`;
        const detail: string =
          r.timeSeconds === null ? '' : ` · ${r.hitsA ?? 0}A ${r.hitsC ?? 0}C ${r.timeSeconds.toFixed(2)}s`;
        return {
          id: r.id,
          index: i + 1,
          value: `${hfLabel}${detail}`,
          unit: null,
          best: r.isBest
        };
      });
    case DrillScoring.RepsOnly:
      return sd.runs.map((r: RunViewModel, i: number): SessionRunRow => {
        const reps: string = r.reps === null ? '-' : r.reps.toString();
        const hasPar: boolean = r.parTimeSeconds !== null;
        return {
          id: r.id,
          index: i + 1,
          value: hasPar ? `${reps} ${repsUnit} · ${r.parTimeSeconds!.toFixed(2)}s par` : `${reps} ${repsUnit}`,
          unit: null,
          best: false
        };
      });
  }
};

/**
 * Najlepszy wynik drilla w formacie podsumowania sesji: RepsOnly = suma powtórzeń,
 * TimePerRep = najlepszy czas, HitFactor = najlepszy HF. Wspólne dla summary i drill-completed.
 */
export const formatBestValue = (sd: SessionDrillViewModel, repsUnit: string): string => {
  if (sd.scoring === DrillScoring.RepsOnly) {
    const total: number = sd.runs.reduce((sum: number, r: RunViewModel): number => sum + (r.reps ?? 0), 0);
    return `${total} ${repsUnit}`;
  }
  const best: RunViewModel | null = sd.best;
  if (best === null) return '-';
  switch (sd.scoring) {
    case DrillScoring.TimePerRep:
      return best.timeSeconds === null ? '-' : `${best.timeSeconds.toFixed(2)}s`;
    case DrillScoring.HitFactor:
      return best.hitFactor === null ? '-' : `HF ${best.hitFactor.toFixed(2)}`;
  }
  return '-';
};
